import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { apiGet, apiPatch, apiPost } from '../../../api/client';
import { Button } from '../../../components/ui/Button';
import { Input } from '../../../components/ui/Input';
import { Section } from '../../../components/ui/Section';

interface NtfyForm {
  ntfyHost: string;
  ntfyTopic: string;
  ntfyToken: string;
  ntfyEnabled: boolean;
  notifyOnContract: boolean;
  notifyOnConfirm: boolean;
  notifyOnDelivery: boolean;
  notifyCollaboratorOnAssign: boolean;
  notifyCollaboratorTopic: string;
  notifyOnPayment: boolean;
  notifyOnPaymentDue: boolean;
  notifyOnNewClient: boolean;
  notifyPreEvent: boolean;
  notifyPreEventDays: number;
}

export default function NtfyNotificationsSection() {
  const qc = useQueryClient();
  const [form, setForm] = useState<NtfyForm>({
    ntfyHost: '', ntfyTopic: '', ntfyToken: '', ntfyEnabled: false,
    notifyOnContract: false, notifyOnConfirm: false, notifyOnDelivery: false,
    notifyCollaboratorOnAssign: false, notifyCollaboratorTopic: '',
    notifyOnPayment: false, notifyOnPaymentDue: false, notifyOnNewClient: false,
    notifyPreEvent: false, notifyPreEventDays: 3,
  });
  const [loaded, setLoaded] = useState(false);
  const [tokenSet, setTokenSet] = useState(false);

  const { data: ntfyData } = useQuery({
    queryKey: ['user-settings-ntfy'],
    queryFn: () => apiGet<NtfyForm & { ntfyTokenSet: boolean }>('/user-settings/ntfy'),
  });

  useEffect(() => {
    if (ntfyData && !loaded) {
      const d = ntfyData as NtfyForm & { ntfyTokenSet: boolean };
      setForm({
        ntfyHost:                   d.ntfyHost ?? '',
        ntfyTopic:                  d.ntfyTopic ?? '',
        ntfyToken:                  '',
        ntfyEnabled:                d.ntfyEnabled ?? false,
        notifyOnContract:           d.notifyOnContract ?? false,
        notifyOnConfirm:            d.notifyOnConfirm ?? false,
        notifyOnDelivery:           d.notifyOnDelivery ?? false,
        notifyCollaboratorOnAssign: d.notifyCollaboratorOnAssign ?? false,
        notifyCollaboratorTopic:    d.notifyCollaboratorTopic ?? '',
        notifyOnPayment:            d.notifyOnPayment ?? false,
        notifyOnPaymentDue:         d.notifyOnPaymentDue ?? false,
        notifyOnNewClient:          d.notifyOnNewClient ?? false,
        notifyPreEvent:             d.notifyPreEvent ?? false,
        notifyPreEventDays:         d.notifyPreEventDays ?? 3,
      });
      setTokenSet(d.ntfyTokenSet ?? false);
      setLoaded(true);
    }
  }, [ntfyData, loaded]);

  const saveMutation = useMutation({
    mutationFn: () => apiPatch('/user-settings/ntfy', {
      ntfyHost:                   form.ntfyHost  || null,
      ntfyTopic:                  form.ntfyTopic || null,
      ntfyToken:                  form.ntfyToken || null,
      ntfyEnabled:                form.ntfyEnabled,
      notifyOnContract:           form.notifyOnContract,
      notifyOnConfirm:            form.notifyOnConfirm,
      notifyOnDelivery:           form.notifyOnDelivery,
      notifyCollaboratorOnAssign: form.notifyCollaboratorOnAssign,
      notifyCollaboratorTopic:    form.notifyCollaboratorTopic || null,
      notifyOnPayment:            form.notifyOnPayment,
      notifyOnPaymentDue:         form.notifyOnPaymentDue,
      notifyOnNewClient:          form.notifyOnNewClient,
      notifyPreEvent:             form.notifyPreEvent,
      notifyPreEventDays:         form.notifyPreEventDays,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['user-settings-ntfy'] });
      setForm(f => ({ ...f, ntfyToken: '' }));
      if (form.ntfyToken) setTokenSet(true);
      toast.success('Impostazioni notifiche push salvate');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const testMutation = useMutation({
    mutationFn: () => apiPost('/user-settings/ntfy/test', {}),
    onSuccess: () => toast.success('Notifica di test inviata!'),
    onError: (e: Error) => toast.error(e.message),
  });

  const toggle = (key: keyof Pick<NtfyForm, 'ntfyEnabled' | 'notifyOnContract' | 'notifyOnConfirm' | 'notifyOnDelivery' | 'notifyCollaboratorOnAssign' | 'notifyOnPayment' | 'notifyOnPaymentDue' | 'notifyOnNewClient' | 'notifyPreEvent'>, label: string) => (
    <label style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
      <input
        type="checkbox"
        checked={form[key] as boolean}
        onChange={e => setForm(f => ({ ...f, [key]: e.target.checked }))}
        style={{ accentColor: 'var(--color-primary)', width: 15, height: 15 }}
      />
      {label}
    </label>
  );

  return (
    <Section title="Notifiche Push (ntfy)">
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          Configura il tuo server ntfy per ricevere notifiche push sullo smartphone o desktop.
        </p>

        {toggle('ntfyEnabled', 'Abilita notifiche push')}

        <Input label="Server ntfy" value={form.ntfyHost} onChange={e => setForm(f => ({ ...f, ntfyHost: e.target.value }))} placeholder="https://ntfy.tuodominio.com" />
        <Input label="Topic" value={form.ntfyTopic} onChange={e => setForm(f => ({ ...f, ntfyTopic: e.target.value }))} placeholder="lenscrm" />
        <Input
          label={tokenSet ? 'Token (lascia vuoto per mantenerlo)' : 'Token di autenticazione'}
          type="password"
          value={form.ntfyToken}
          onChange={e => setForm(f => ({ ...f, ntfyToken: e.target.value }))}
          placeholder={tokenSet ? '••••••••' : 'tk_...'}
        />

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.25rem' }}>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Notifica quando:
          </p>
          {toggle('notifyOnContract', 'Contratto firmato (stato → Contratto)')}
          {toggle('notifyOnConfirm',  'Evento confermato (stato → Confermato)')}
          {toggle('notifyOnDelivery', 'Materiale consegnato (stato → Consegnato)')}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.25rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Pagamenti e clienti
          </p>
          {toggle('notifyOnPayment', 'Pagamento ricevuto')}
          {toggle('notifyOnPaymentDue', 'Pagamento in scadenza')}
          {toggle('notifyOnNewClient', 'Nuovo cliente aggiunto')}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.25rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Promemoria pre-evento
          </p>
          {toggle('notifyPreEvent', 'Promemoria pre-evento')}
          {form.notifyPreEvent && (
            <Input
              label="Giorni prima dell'evento"
              type="number"
              min={1}
              max={90}
              value={String(form.notifyPreEventDays)}
              onChange={e => setForm(f => ({ ...f, notifyPreEventDays: Math.max(1, Math.min(90, parseInt(e.target.value) || 1)) }))}
              style={{ maxWidth: 120 }}
            />
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', paddingTop: '0.25rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
          <p style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            Collaboratori
          </p>
          {toggle('notifyCollaboratorOnAssign', 'Collaboratore assegnato a evento')}
          <Input
            label="Topic collaboratori (opzionale)"
            value={form.notifyCollaboratorTopic}
            onChange={e => setForm(f => ({ ...f, notifyCollaboratorTopic: e.target.value }))}
            placeholder={form.ntfyTopic || 'lenscrm-collab'}
            hint="Se vuoto, usa il topic principale"
          />
        </div>

        <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap', paddingTop: '0.25rem' }}>
          <Button onClick={() => saveMutation.mutate()} loading={saveMutation.isPending}>
            Salva impostazioni
          </Button>
          <Button variant="ghost" onClick={() => testMutation.mutate()} loading={testMutation.isPending} disabled={!form.ntfyHost || !form.ntfyTopic}>
            Testa notifica
          </Button>
        </div>
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
          Salva prima le impostazioni, poi usa "Testa notifica" per verificare la connessione.
        </p>
      </div>
    </Section>
  );
}
